import { FC, useCallback, useEffect, useRef, useState } from 'react';
import SingleMonthView from '../../../../app/modules/Dashboard/calendar/component/SingleMonthView';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../redux/store';
import { fetchEventAction, fetchEventCategoryAndColorsAction } from '../../../../redux/actions/events.actions';
import {
    getCalenderEvents,
    getEventCategoryAndColors,
    getPublicCalendarJsonData,
} from '../../../../app/modules/Dashboard/calendar/core/request';
import { useSnackbar } from '../../../../_theme/context/SnackbarContext';
import { useAuth } from '../../../../app/modules/auth';
import { useLocation, useNavigate } from 'react-router-dom';
import { IEvent } from '../../../../app/modules/Dashboard/calendar/core/type';
import { getEventEndDateTime, getEventStartDateTime } from '../../../../app/modules/Dashboard/calendar/utils';
import EventModal from '../../../../app/modules/Dashboard/calendar/component/EventModal';
import AddorUpdateModal from '../../../../app/modules/Dashboard/calendar/component/AddorUpdateModal';
import clsx from 'clsx';
import { getApiErrorMessage } from "../../../../utils/utils";
import { isAbortedError } from "../../../../utils/global";

const MOBILE_BREAKPOINT = '(max-width: 576px)';

interface Props {
    onClose: () => void
    currentDate?: Date
    setCurrentDate?: React.Dispatch<React.SetStateAction<Date>>
    /** When true, render as a visible panel instead of a KT submenu. */
    embedded?: boolean
}

const CalendarData: FC<Props> = ({
    onClose,
    currentDate,
    setCurrentDate,
    embedded = false,
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const { showSnackbar } = useSnackbar();
    const { auth } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const abortRef = useRef<AbortController | null>(null);

    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [eventsForModal, setEventsForModal] = useState<IEvent[]>([]);
    const [showAddEventModal, setShowAddEventModal] = useState<boolean>(false);
    const [isUpdateMode, setIsUpdateMode] = useState<boolean>(false);
    const [selectedEvent, setSelectedEvent] = useState<IEvent | null>(null);
    const [isMobile, setIsMobile] = useState<boolean>(() =>
        typeof window !== 'undefined'
            ? window.matchMedia(MOBILE_BREAKPOINT).matches
            : false
    );

    const [internalDate, setInternalDate] = useState<Date>(() => {
        return currentDate instanceof Date && !isNaN(currentDate.getTime())
            ? new Date(currentDate)
            : new Date();
    });

    const { eventData, isEventDataLoaded } = useSelector(
        (state: RootState) => state.eventReducer
    );

    useEffect(() => {
        const mediaQuery = window.matchMedia(MOBILE_BREAKPOINT);
        const handleChange = (event: MediaQueryListEvent) => {
            setIsMobile(event.matches);
        };

        setIsMobile(mediaQuery.matches);
        mediaQuery.addEventListener('change', handleChange);

        return () => {
            mediaQuery.removeEventListener('change', handleChange);
        };
    }, []);

    useEffect(() => {
        if (currentDate instanceof Date && !isNaN(currentDate.getTime())) {
            setInternalDate(new Date(currentDate));
        }
    }, [currentDate]);

    const handleNavigate = useCallback((day: Date) => {
        navigate(auth ? `/manage-events/?date=${day}` : `/pages/manage-events/?date=${day}`, {
            state: {
                from: location.pathname
            }
        });
        onClose();
    }, [navigate, auth, onClose, location.pathname]);

    const handleFetchEventsData = useCallback(async () => {
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        try {
            if (auth) {
                const [eventsResponse, categoriesResponse] = await Promise.all([
                    getCalenderEvents({}, controller.signal),
                    getEventCategoryAndColors(controller.signal),
                ]);

                if (controller.signal.aborted) return;

                if (eventsResponse.status === 'ok') {
                    dispatch(fetchEventAction(eventsResponse.data ?? []));
                } else {
                    showSnackbar(eventsResponse.message, 'danger');
                }

                if (categoriesResponse.status === 'ok') {
                    dispatch(fetchEventCategoryAndColorsAction(categoriesResponse.data ?? []));
                } else {
                    showSnackbar(categoriesResponse.message, 'danger');
                }
            } else {
                const response = await getPublicCalendarJsonData(controller.signal);
                if (controller.signal.aborted) return;
                dispatch(fetchEventAction(response.events ?? []));
                dispatch(fetchEventCategoryAndColorsAction(response.categories ?? []));
            }
        } catch (error) {
            if (isAbortedError(error)) return;
            showSnackbar(getApiErrorMessage(error, "Failed to fetch events"), "danger");
        }
    }, [dispatch, auth, showSnackbar]);

    const handleDateClick = useCallback((day: Date) => {
        const dayEvents = eventData.filter((event: IEvent) => {
            const eventStart = getEventStartDateTime(event);
            const eventEnd = getEventEndDateTime(event);

            eventStart.setHours(0, 0, 0, 0);
            eventEnd.setHours(23, 59, 59, 999);

            const selected = new Date(day);
            selected.setHours(0, 0, 0, 0);

            return selected >= eventStart && selected <= eventEnd;
        });

        setSelectedDate(day);
        setEventsForModal(dayEvents);
        setModalOpen(true);
    }, [eventData]);

    const handleModalClose = useCallback(() => {
        setModalOpen(false);
    }, []);

    const handleAddEvent = useCallback(() => {
        setIsUpdateMode(false);
        setSelectedEvent(null);
        setSelectedDate(internalDate);
        setShowAddEventModal(true);
    }, [internalDate]);

    const handleAddEventModalHide = useCallback(() => {
        setShowAddEventModal(false);
        setIsUpdateMode(false);
        setSelectedEvent(null);
    }, []);

    const handleDateChange = useCallback((newDate: Date) => {
        if (newDate instanceof Date && !isNaN(newDate.getTime())) {
            const nextDate = new Date(newDate);
            setInternalDate(nextDate);
            if (setCurrentDate) {
                setCurrentDate(nextDate);
            }
        }
    }, [setCurrentDate]);

    useEffect(() => {
        if (!isEventDataLoaded) {
            handleFetchEventsData();
        }
        return () => {
            abortRef.current?.abort();
        };
    }, [handleFetchEventsData, isEventDataLoaded]);

    // Guard against invalid date
    if (!(internalDate instanceof Date) || isNaN(internalDate.getTime())) {
        return null;
    }

    return (
        <div
            className={clsx(
                'menu-column rounded-4 fs-6 w-auto',
                !embedded && 'menu menu-sub menu-sub-dropdown'
            )}
            {...(!embedded ? { 'data-kt-menu': 'true' } : {})}
            style={{ background: 'transparent', boxShadow: 'none' }}
            onClick={(e) => e.stopPropagation()}
        >
            <SingleMonthView
                currentDate={internalDate}
                setCurrentDate={handleDateChange}
                events={eventData}
                onDateClick={handleDateClick}
                onNavigate={isMobile ? undefined : handleNavigate}
                onAddEvent={isMobile && auth ? handleAddEvent : undefined}
            />
            <EventModal
                show={modalOpen}
                onHide={handleModalClose}
                date={selectedDate}
                events={eventsForModal}
                setSelectedEvent={setSelectedEvent}
                setIsUpdateMode={setIsUpdateMode}
                setShowAddEventModal={setShowAddEventModal}
                setEventsForModal={setEventsForModal}
                selectedEvent={selectedEvent}
                isAllowActions={Boolean(auth)}
            />
            {auth && (
                <AddorUpdateModal
                    show={showAddEventModal}
                    onHide={handleAddEventModalHide}
                    isUpdateMode={isUpdateMode}
                    event={selectedEvent}
                    totalEvents={eventData.length}
                    selectedDate={selectedDate}
                />
            )}
        </div>
    );
};

export { CalendarData };
